import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { AppDataSelectComponent } from '@ukam/ui-kit/inputs/selects/data-select/data-select.component';
import { TablerIconComponent } from '@ukam/ui-kit/icon/tabler-icon.component';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterBaseService } from '../../services/filter-base.service';
import { FilterSearchService } from '../../services/filter-search.service';
import { FilterSupportService } from '../../services/filter-support.service';
import { AppBoolFilterComponent } from '../bool-filter/bool-filter.component';
import { AppDateFilterComponent } from '../date-filter/date-filter.component';
import { AppDateTimeFilterComponent } from '../datetime-filter/datetime-filter.component';
import { AppDecimalFilterComponent } from '../decimal-filter/decimal-filter.component';
import { AppGuidFilterComponent } from '../guid-filter/guid-filter.component';
import { AppIntFilterComponent } from '../int-filter/int-filter.component';
import { AppListFilterComponent } from '../list-filter/list-filter.component';
import { AppStringFilterComponent } from '../string-filter/string-filter.component';


@Component({
  selector: 'app-feature-filter',
  templateUrl: './feature-filter.component.html',
  styles: [':host ::ng-deep .hide-drag-handle [cdkDragHandle] { display: none; }'],
  imports: [
    MaterialModule,
    NgScrollbarModule,
    FormsModule,
    TranslocoModule,
    MatCheckboxModule,
    CommonModule,
    RouterModule,
    AppDateFilterComponent,
    AppDateTimeFilterComponent,
    AppStringFilterComponent,
    AppIntFilterComponent,
    AppDecimalFilterComponent,
    AppGuidFilterComponent,
    AppBoolFilterComponent,
    AppListFilterComponent,
    CdkDropList,
    CdkDrag,
  ],
})
export class AppFeatureFilterComponent implements OnInit, OnDestroy {
  @ViewChild('descriptionInput') descriptionInput!: ElementRef<HTMLInputElement>;


  @Input({ required: true }) feature = '';

  searchService = inject(FilterSearchService);
  sidePanelOpened = signal(true);

  service = inject(FilterBaseService);

  private router = inject(Router);
  private route = inject(ActivatedRoute);

  searchTerm = signal('');

  showFields = signal(false);
  editMode = signal(false);

  private normalize(value: string): string {
    return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  }

  filteredFeatureFields = computed(() => {
    const term = this.normalize(this.searchTerm().trim());
    const addedKeys = new Set((this.service.model()?.fields ?? []).map(f => f.key));
    const items: any[] = (this.service.fields() ?? []).filter((item: any) => !addedKeys.has(item.key));
    if (!term) return items;
    return items.filter((item: any) => item.label && this.normalize(item.label).includes(term));
  });

  constructor() {
    // let id = '019F30D1-F6DC-7264-9CAC-8FA7658865DA';
  }

  ngOnInit(): void {
    const model = this.service.model();
    if (!model)
      this.service.getByFeature(this.feature);
    else
      this.service.fieldsChange.next(model.fields ?? []);

    this.service.onSaved$.subscribe(() => {
      this.editMode.set(false);
    });
  }

  close(): void {
    this.service.clear();
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  ngOnDestroy(): void {
    // this.service.clear();
  }


  isOver(): boolean {
    return window.innerWidth < 1199;
  }

  navigateNew(): void {
    this.router.navigate(['new'], { relativeTo: this.route });
  }

  selectField(field: any): void {
    const model = this.service.model();
    if (!model || !field?.key) return;

    const fields = model.fields ?? [];
    if (fields.some(f => f.key === field.key)) return;

    const mapped: FilterField = {
      key: field.key,
      name: field.name,
      label: field.label,
      type: field.type,
      operator: field.operator,
      sourceType: field.sourceType,
      source: field.source,
    };

    this.service.setFields([...fields, mapped], false);
  }

  removeField(index: number): void {
    const model = this.service.model();
    if (!model) return;

    const fields = [...(model.fields ?? [])];
    fields.splice(index, 1);

    this.service.setFields(fields);
  }

  updateField(index: number, field: FilterField): void {
    const model = this.service.model();
    if (!model) return;

    const fields = [...(model.fields ?? [])];
    fields[index] = field;

    this.service.setFields(fields);
  }

  clearFields(): void {
    const model = this.service.model();
    if (!model) return;

    const fields = (model.fields ?? []).map(field => ({
      ...field,
      value: undefined,
      values: [],
      start: undefined,
      end: undefined,
      items: (field.items ?? []).map((item: any) => ({ ...item, selected: false })),
    }));

    this.service.model.set({ ...model, searchText: '' });
    this.service.setFields(fields);
  }

  removeAllFields(): void {
    this.service.setFields([]);
  }

  // Maps a field's key to the API path its list-filter dialog should search against.
  // Extend as more codekeyname/reference fields get activated in the backend metadata.

  reorderFields(event: CdkDragDrop<FilterField[]>): void {
    const model = this.service.model();
    if (!model) return;

    const fields = [...(model.fields ?? [])];
    moveItemInArray(fields, event.previousIndex, event.currentIndex);
    this.service.setFields(fields);
  }
}
